import { Add, type BlockStyle, Button, FormCard, RichEditor } from '@lmc/ui-kit'
import { prepareRichEditorContent } from '@lmc/ui-kit/src/dataEntry/RichEditor/normalizeRichEditorHtmlForStorage'
import { Alert } from 'antd'
import { showToast } from 'common/utils'
import { hasMeaningfulStoredHtml } from 'common/utils/html'
import Field from 'components/fields'
import { ViewStoredHtml } from 'components/generic/ViewStoredHtml'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  type SuggestionDecision,
  useAcceptedSuggestionNeedIds
} from 'hooks/useNeedSuggestions'
import useResidentNeeds from 'hooks/useResidentNeeds'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  FormCardHeading,
  NotesBlock,
  NotesLabel,
  NotesText,
  TextareaWrapper
} from '../../../../components/carePlans/carePlanSharedStyles'
import type {
  PopulatedResidentNeed,
  ResidentNeedSnapshot
} from '../../../../shared/hooks/useCarePlanPopulated'
import AddNeedsModal from './AddNeedsModal'
import NeedsList from './NeedsList'
import RemoveNeedModal from './RemoveNeedModal'
import StagedNeedItem from './StagedNeedItem'
import SuggestedNeedsPanel from './SuggestedNeedsPanel'

const CARE_PLAN_BLOCK_STYLES: BlockStyle[] = [
  'paragraph',
  'bulletList',
  'orderedList'
]

interface NeedsSectionProps {
  residentId: string
  carePlanAdlId?: string
  residentNeeds?: PopulatedResidentNeed[]
  residentNeedsSnapshot?: ResidentNeedSnapshot[]
  value?: string[]
  disabled?: boolean
  onChange?: (needIds: string[]) => void
  isRevision?: boolean
  lastReviewDate?: string
  notesValue?: string | null
  notesHtmlValue?: string | null
  onChangeNotesHtml?: (html?: string) => void
  formId?: string | null
  fields?: object
  skillNeedsContent?: React.ReactNode
  /** Staged suggestion decisions, owned by the review form (committed on
   * save, discarded on cancel). Absent = suggestions UI hidden. */
  suggestionDecisions?: SuggestionDecision[]
  onChangeSuggestionDecisions?: (next: SuggestionDecision[]) => void
}

const NeedsSection: React.FC<NeedsSectionProps> = ({
  residentId,
  carePlanAdlId,
  residentNeeds = [],
  residentNeedsSnapshot = [],
  value,
  disabled = false,
  onChange,
  isRevision = false,
  lastReviewDate,
  notesValue,
  notesHtmlValue,
  onChangeNotesHtml,
  formId,
  fields,
  skillNeedsContent,
  suggestionDecisions = [],
  onChangeSuggestionDecisions
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [removeNeedId, setRemoveNeedId] = useState<string | null>(null)

  const residentFirstName: string | undefined = useSelector(
    (state: {
      data: { residents?: { id: string; name?: { first?: string } }[] }
    }) => state.data.residents?.find((r) => r.id === residentId)?.name?.first
  )

  const carePlanNominative: string | undefined = useSelector(
    (state: { global: { home?: { preferredCarePlansNominative?: string } } }) =>
      state.global.home?.preferredCarePlansNominative
  )

  // Needed to display newly-added needs not yet in the populated response
  const { needs: allResidentNeeds } = useResidentNeeds({
    id: !disabled && !isRevision ? residentId : ''
  })

  const { data: suggestionFlags } = useFeatureFlags({
    flagNames: ['initial_assessments_needs_suggestions_review']
  })
  const suggestionsFlagEnabled =
    suggestionFlags.initial_assessments_needs_suggestions_review?.enabled ??
    false

  // Provenance marker: needs that originated from an accepted AI suggestion
  // carry an "AI-assisted" tag wherever this list renders (edit AND view).
  const aiNeedIds = useAcceptedSuggestionNeedIds({
    residentId,
    enabled: suggestionsFlagEnabled && !!residentId
  })

  const effectiveIds = value ?? residentNeeds.map((n) => n.id)

  const displayedNeeds: PopulatedResidentNeed[] = isRevision
    ? residentNeedsSnapshot
    : disabled
      ? effectiveIds.length > 0
        ? residentNeeds.filter((n) => effectiveIds.includes(n.id))
        : residentNeeds
      : (effectiveIds
          .map((needId) => {
            const populated = residentNeeds.find((n) => n.id === needId)
            if (populated) return populated
            const resident = allResidentNeeds.find((n) => n.id === needId)
            if (resident) {
              return {
                id: resident.id,
                need: resident.need,
                notes: resident.notes,
                addedOn: resident.addedOn,
                addedBy: resident.addedBy,
                editedOn: resident.editedOn,
                editedBy: resident.editedBy
              }
            }
            return null
          })
          .filter(Boolean) as PopulatedResidentNeed[])

  const handleOpenAddModal = () => {
    setIsAddModalVisible(true)
  }

  const handleAddNeeds = (selectedIds: string[]) => {
    onChange?.([...effectiveIds, ...selectedIds])
    showToast({ title: t(carePlansConstants.NEEDS_ADDED_TOAST) })
  }

  const handleRemoveClick = (needId: string) => {
    setRemoveNeedId(needId)
  }

  const handleRemoveConfirm = () => {
    if (removeNeedId) {
      const updatedIds = effectiveIds.filter((id) => id !== removeNeedId)
      onChange?.(updatedIds)
      showToast({ title: t(carePlansConstants.NEED_REMOVED_TOAST) })
    }
    setRemoveNeedId(null)
  }

  const handleRemoveCancel = () => {
    setRemoveNeedId(null)
  }

  // Used suggestions render as draft rows in the linked-needs list until the
  // review is saved; Cancel discards them.
  const stagedAdds =
    !disabled && !isRevision
      ? suggestionDecisions.filter((d) => d.action === 'add')
      : []

  const hasNeeds = displayedNeeds.length > 0
  const hasNoSnapshotData = isRevision && residentNeedsSnapshot.length === 0

  return (
    <>
      <StyledFormCard
        title={
          <FormCardHeading level={3}>
            {t(carePlansConstants.NEEDS_SECTION_TITLE)}
          </FormCardHeading>
        }
        rightChildren={
          !disabled &&
          !isRevision && (
            <Button
              type="button"
              variant="text"
              icon={<Add />}
              onClick={handleOpenAddModal}
            >
              {t(carePlansConstants.ADD_NEEDS_BUTTON)}
            </Button>
          )
        }
      >
        {hasNoSnapshotData ? (
          <Alert
            message={t(carePlansConstants.NEEDS_NO_SNAPSHOT)}
            type="info"
            showIcon
          />
        ) : hasNeeds || stagedAdds.length > 0 ? (
          <NeedsList
            items={displayedNeeds}
            disabled={disabled}
            lastReviewDate={lastReviewDate}
            onRemove={!disabled ? handleRemoveClick : undefined}
            aiNeedIds={aiNeedIds}
            testId="care-plan-needs-list"
          >
            {stagedAdds.map((decision) => (
              <StagedNeedItem
                key={decision.suggestion.id}
                decision={decision}
              />
            ))}
          </NeedsList>
        ) : undefined}
        {skillNeedsContent}
        {(formId || notesHtmlValue || notesValue) && (
          <NotesBlock>
            <NotesLabel weight="medium">Notes</NotesLabel>
            {formId && onChangeNotesHtml && !disabled ? (
              <div style={richEditorWrapperStyle}>
                <Suspense fallback={<div>Loading...</div>}>
                  <RichEditor
                    html={notesHtmlValue ?? ''}
                    onChangeHtml={onChangeNotesHtml}
                    blockStyles={CARE_PLAN_BLOCK_STYLES}
                  />
                </Suspense>
              </div>
            ) : formId && !disabled ? (
              <TextareaWrapper $withNotesLabel>
                <Suspense fallback={<div>Loading...</div>}>
                  <Field
                    keys="needs"
                    // @ts-ignore
                    label="Needs"
                    layout="no-label"
                    formId={formId}
                    style={textareaStyle}
                    type="textarea"
                    values={fields}
                  />
                </Suspense>
              </TextareaWrapper>
            ) : hasMeaningfulStoredHtml(notesHtmlValue) ? (
              <ViewStoredHtml
                html={prepareRichEditorContent(notesHtmlValue ?? undefined)}
              />
            ) : (
              <NotesText>{notesValue}</NotesText>
            )}
          </NotesBlock>
        )}
        {!disabled && !isRevision && onChangeSuggestionDecisions && (
          <SuggestedNeedsPanel
            residentId={residentId}
            carePlanAdlId={carePlanAdlId}
            residentFirstName={residentFirstName}
            carePlanNominative={carePlanNominative}
            decisions={suggestionDecisions}
            onChangeDecisions={onChangeSuggestionDecisions}
          />
        )}
      </StyledFormCard>
      {!disabled && !isRevision && (
        <AddNeedsModal
          isModalVisible={isAddModalVisible}
          setIsModalVisible={setIsAddModalVisible}
          residentId={residentId}
          linkedNeedIds={effectiveIds}
          onConfirm={handleAddNeeds}
        />
      )}
      <RemoveNeedModal
        isModalVisible={!!removeNeedId}
        setIsModalVisible={(visible) => {
          if (!visible) handleRemoveCancel()
        }}
        onConfirm={handleRemoveConfirm}
        onCancel={handleRemoveCancel}
      />
    </>
  )
}

const StyledFormCard = styled(FormCard)`
  .ant-divider {
    margin: 16px 0 !important;
  }
`

const richEditorWrapperStyle = {
  marginTop: 8
}

const textareaStyle = {
  minHeight: 100,
  padding: '12px 16px',
  resize: 'vertical' as const,
  borderRadius: 8,
  width: '100%'
}

export default NeedsSection
